// PublicInvoicePay — the SHAREABLE HHC pay page (/pay/:token). No login: the
// unguessable token authorizes viewing + paying exactly one invoice. Shows the
// invoice, opens the PDF (and the payment receipt once paid), and mints a
// fresh gateway order per Pay click; after the gateway redirect
// (?from=gateway) it polls status — the status endpoint verifies the capture
// server-side — until the invoice reads paid. Links expire after 7 days.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import HostedCardPanel, { warmHostedSession } from './payments/HostedCardPanel';
import SavedCardPicker from './ClientWallet/SavedCardPicker';
import SavedCardPay from './payments/SavedCardPay';
import PayNowPanel from './PayNowPanel';
import './ClientWallet/savedCards.css';
import './publicInvoicePay.css';

const money = (n) => `AED ${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

const IconDoc = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
    </svg>
);
const IconReceipt = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M21 21V4a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v17l3-2 3 2 3-2 3 2 3-2z" />
        <line x1="7" y1="8" x2="17" y2="8" />
        <line x1="7" y1="12" x2="13" y2="12" />
    </svg>
);
const IconLock = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);
const IconCheck = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path d="m8.5 12.5 2.5 2.5 5-5.5" />
    </svg>
);

export default function PublicInvoicePay() {
    const { token } = useParams();
    const [params] = useSearchParams();
    const fromGateway = params.get('from') === 'gateway';

    const [info, setInfo] = useState(null);
    const [error, setError] = useState('');
    const [starting, setStarting] = useState(false);
    const [checking, setChecking] = useState(fromGateway);
    const pollRef = useRef(null);

    /* LOGGED-IN OWNER (2026-09-02): when the viewer is the invoice's own
       client, the page grows the full client checkout — wallet balance,
       saved cards (CVV + 3DS relay), and save-this-card on a new card. A
       guest, or anybody else's client, sees exactly the page as before. */
    const [ctx, setCtx] = useState(null);
    const [cardSelection, setCardSelection] = useState('new');
    const [savedCvv, setSavedCvv] = useState('');
    const [savedCardBusy, setSavedCardBusy] = useState(false);
    const [saveNewCard, setSaveNewCard] = useState(false);
    const [walletPaying, setWalletPaying] = useState(false);
    const [useWallet, setUseWallet] = useState(true);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`/api/pay-link/${token}/status`);
            setInfo(res.data);
            setError('');
            return res.data;
        } catch (e) {
            setError(e.response?.data?.error || 'This payment link is not valid.');
            return null;
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        let alive = true;
        axios.get(`/api/pay-link/${token}/client-context`)
            .then((r) => {
                if (!alive) return;
                setCtx(r.data || { logged_in: false });
                const cards = Array.isArray(r.data?.cards) ? r.data.cards : [];
                if (cards.length > 0) setCardSelection(cards[0].id);
            })
            .catch(() => { if (alive) setCtx({ logged_in: false }); });
        return () => { alive = false; };
    }, [token]);

    /* Fetch the gateway SDK while they read the invoice — see
       warmHostedSession. Nothing here depends on it having worked. */
    useEffect(() => {
        warmHostedSession(`/api/pay-link/${token}/hosted-session/config`);
    }, [token]);

    // Post-gateway: poll until paid (max ~2 min), then stop.
    useEffect(() => {
        if (!fromGateway) return undefined;
        let tries = 0;
        let alive = true; // an awaited tick may resolve after unmount
        pollRef.current = setInterval(async () => {
            tries += 1;
            const d = await load();
            if (!alive) return;
            if ((d && d.paid) || tries >= 24) {
                clearInterval(pollRef.current);
                setChecking(false);
            }
        }, 5000);
        return () => { alive = false; clearInterval(pollRef.current); };
    }, [fromGateway, load]);

    const startPayment = async () => {
        setStarting(true);
        setError('');
        try {
            const res = await axios.post(`/api/pay-link/${token}/checkout`);
            window.location.assign(res.data.payment_url);
        } catch (e) {
            setError(e.response?.data?.error || 'Could not start the payment. Please try again.');
            setStarting(false);
            load(); // same reason as the embedded rail below
        }
    };

    // --- EMBEDDED card rail (Hosted Session, 2026-08-14) ---------------------
    // The payer types their card HERE, on the HHC page they were sent, and 3DS
    // runs in an overlay on it. Nothing about the link changes: the token is
    // still the whole authorization, the balance is still read live server-side
    // per attempt, and the capture still settles through
    // settleInvoiceLinkFromCapture. New card only — a guest has no account, so
    // there are no saved cards and nothing to save one to.
    const [hostedRail, setHostedRail] = useState('probe');
    const [paidHere, setPaidHere] = useState(false);
    // TWO STEPS (2026-08-14): 'choose' is the invoice and the two actions, 'card'
    // is the form on its own. Putting the card fields under the summary made a
    // long page for what is, at that moment, a single decision.
    const [step, setStep] = useState('choose');
    // The card panel's Pay button is portalled down into the step-2 action row,
    // so Back and Pay are two halves of one row rather than two stacked bars.
    const [paySlot, setPaySlot] = useState(null);

    const owner = !!ctx?.logged_in;
    const savedCards = owner && Array.isArray(ctx?.cards) ? ctx.cards : [];
    const walletCeiling = owner ? Number(ctx?.wallet?.ceiling || 0) : 0;
    const selectedSavedCard = savedCards.find((c) => c.id === cardSelection) || null;
    const savedCardActive = owner && typeof cardSelection === 'number' && !!selectedSavedCard;
    // The split — the same one toggle + derived rail every client checkout
    // uses (owner, 2026-09-02: "the wallet should be a toggle; sometimes it
    // doesn't cover the full amount"). Server-clamped again at checkout.
    const balanceDue = Number(info?.balance || 0);
    const walletOn = owner && useWallet && walletCeiling > 0.005;
    const walletPortion = walletOn ? Math.min(walletCeiling, balanceDue) : 0;
    const cardPortion = Math.max(0, Math.round((balanceDue - walletPortion) * 100) / 100);
    const walletCovers = walletOn && cardPortion <= 0.009 && walletPortion > 0.009;
    const walletPortionRef = useRef(0);
    walletPortionRef.current = walletPortion;

    const payFromWallet = useCallback(async () => {
        setWalletPaying(true);
        setError('');
        try {
            const res = await axios.post(`/api/pay-link/${token}/pay-wallet`);
            if (res.data?.ok) {
                setPaidHere(true);
                load();
            } else {
                setError('Could not complete the wallet payment. Please try again.');
            }
        } catch (e) {
            setError(e.response?.data?.error || 'Could not complete the wallet payment. Please try again.');
            load();
        } finally {
            setWalletPaying(false);
        }
    }, [token, load]);

    const chargeSavedCard = useCallback(
        (cvv) => axios.post(`/api/pay-link/${token}/pay-saved-card`, {
            payment_method_id: cardSelection,
            cvv,
            wallet_portion: walletPortionRef.current,
        }).then((r) => r.data),
        [token, cardSelection]
    );

    const startHostedPayment = useCallback(async (sessionId) => {
        setError('');
        try {
            const res = await axios.post(`/api/pay-link/${token}/checkout`, {
                session_id: sessionId,
                wallet_portion: walletPortionRef.current,
                ...(owner && saveNewCard ? { save_card: true, save_card_consent: true } : {}),
            });
            if (!res.data?.payment_response) {
                setError('Could not start the payment. Please try again.');
                return null;
            }
            return res.data;
        } catch (e) {
            setError(e.response?.data?.error || 'Could not start the payment. Please try again.');
            // Re-read the invoice: the commonest reason a checkout is refused is
            // that the balance closed while this page was open (paid another
            // way, recorded in Zoho by hand). The status endpoint reads Zoho
            // live, so this turns the form into the paid view instead of
            // leaving a payer staring at an error over an "amount due".
            load();
            return null;
        }
    }, [token, load, owner, saveNewCard]);

    const verifyHostedPayment = useCallback(
        (orderRef) => axios.post(`/api/pay-link/${token}/verify`, { order_ref: orderRef }).then((r) => r.data),
        [token]
    );

    const expired = !!info?.expired;
    // A payment verified by the panel is PAID even if the status re-read has not
    // caught up yet — the money moved and the server said so.
    const paid = !!info?.paid || paidHere;

    return (
        <div className="pubpay">
            <div className="pubpay__card">
                <header className="pubpay__brandBlock">
                    <a
                        className="pubpay__logoLink"
                        href="https://holidayhomecleaning.ae"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Holiday Home Cleaning website"
                    >
                        <img className="pubpay__logo" src="/logo.png" alt="Holiday Home Cleaning" />
                    </a>
                    <div className="pubpay__brand">Holiday Home Cleaning Services Co. L.L.C</div>
                </header>

                {!info && !error && <div className="pubpay__loading">Loading your invoice…</div>}
                {error && <div className="pubpay__error">{error}</div>}

                {info && expired && (
                    <div className="pubpay__error">
                        This payment link has expired.
                        <br />
                        Please ask Holiday Home Cleaning for a new link.
                    </div>
                )}

                {info && !expired && (
                    <>
                        <div className="pubpay__identity">
                            <div className="pubpay__invoiceLabel">Invoice</div>
                            <div className="pubpay__title">
                                {info.invoice_number || ''}
                                {info.guest_name ? <span className="pubpay__guest">for {info.guest_name}</span> : null}
                            </div>
                        </div>

                        <div className="pubpay__amountWrap">
                            {paid ? (
                                <>
                                    <div className="pubpay__amountLabel">Amount paid</div>
                                    <div className={'pubpay__amount is-paid'}>{money(info.total)}</div>
                                </>
                            ) : (
                                <>
                                    <div className="pubpay__amountLabel">Amount due</div>
                                    <div className="pubpay__amount">{money(info.balance)}</div>
                                </>
                            )}
                        </div>

                        {paid ? (
                            <>
                                <div className="pubpay__paidBadge">
                                    <IconCheck />
                                    Paid in full — thank you!
                                </div>
                                <div className="pubpay__actions">
                                    <a
                                        className="pubpay__btn pubpay__btn--ghost"
                                        href={`/api/pay-link/${token}/pdf`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <IconDoc />
                                        View invoice
                                    </a>
                                    <a
                                        className="pubpay__btn pubpay__btn--ghost"
                                        href={`/api/pay-link/${token}/receipt`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <IconReceipt />
                                        Payment receipt
                                    </a>
                                </div>
                            </>
                        ) : checking ? (
                            <div className="pubpay__checking">Confirming your payment…</div>
                        ) : step === 'card' && hostedRail !== 'off' ? (
                            /* STEP 2 — the card form on its own. It lives on THIS
                               page: no redirect, and 3DS runs in its own overlay.
                               The panel downgrades itself to the redirect button
                               if the SDK cannot mount — a payer who was sent a
                               link must never meet a broken form. Keyed on the
                               balance so a figure that moves builds a new form
                               rather than re-labelling one Apple Pay has already
                               been told the old amount for. */
                            <>
                                <HostedCardPanel
                                    key={`pl-${(owner ? cardPortion : Number(info.balance || 0)).toFixed(2)}`}
                                    payLabel={`Pay ${money(owner ? cardPortion : info.balance)}`}
                                    amountAed={owner ? cardPortion : Number(info.balance || 0)}
                                    configUrl={`/api/pay-link/${token}/hosted-session/config`}
                                    startPayment={startHostedPayment}
                                    verifyPayment={verifyHostedPayment}
                                    onUnavailable={() => setHostedRail('off')}
                                    onPaid={() => { setPaidHere(true); load(); }}
                                    onFailed={() => { /* the panel states the outcome and re-arms itself */ }}
                                    actionSlot={paySlot}
                                >
                                    {owner && (
                                        <label className="hcard-save">
                                            <input
                                                type="checkbox"
                                                checked={saveNewCard}
                                                onChange={(e) => setSaveNewCard(e.target.checked)}
                                            />
                                            <span>Save this card for future payments</span>
                                        </label>
                                    )}
                                </HostedCardPanel>
                                <div className="pubpay__actions">
                                    <button
                                        type="button"
                                        className="pubpay__btn pubpay__btn--ghost"
                                        onClick={() => setStep('choose')}
                                    >
                                        Back
                                    </button>
                                    <div className="pubpay__paySlot" ref={setPaySlot} />
                                </div>
                            </>
                        ) : (
                            /* STEP 1 — pay, or read the invoice first. The
                               logged-in OWNER additionally gets the full client
                               checkout: wallet, saved cards, new card. A guest
                               sees exactly the two-button row as before. */
                            <>
                                {/* Nothing on the account → no box (owner,
                                    2026-09-02): the page simply pays by card. */}
                                {owner && (walletCeiling > 0.005 || savedCards.length > 0) && (
                                    <div className="pubpay__owner">
                                        <div className="pubpay__ownerHead">
                                            <IconCheck />
                                            Signed in — pay with your account
                                        </div>

                                        {/* THE ONE CHECKOUT BLOCK — the same
                                            figures + toggle every client
                                            checkout renders. */}
                                        {walletCeiling > 0.005 && (
                                            <PayNowPanel
                                                amount={balanceDue}
                                                wallet={ctx?.wallet}
                                                spendable={walletCeiling}
                                                useWallet={useWallet}
                                                onUseWallet={setUseWallet}
                                                onTopUp={null}
                                                label="This invoice"
                                                disabled={walletPaying || savedCardBusy}
                                            />
                                        )}

                                        {!walletCovers && savedCards.length > 0 && (
                                            <SavedCardPicker
                                                cards={savedCards}
                                                value={cardSelection}
                                                onChange={setCardSelection}
                                                allowSaveNew={false}
                                                disabled={walletPaying || savedCardBusy}
                                                cvvValue={savedCardActive ? savedCvv : null}
                                                onCvvChange={savedCardActive ? setSavedCvv : null}
                                                newCardSub="Continue to input card details."
                                            />
                                        )}

                                        {!walletCovers && savedCardActive && (
                                            <SavedCardPay
                                                card={selectedSavedCard}
                                                compact
                                                cvv={savedCvv}
                                                onCvvChange={setSavedCvv}
                                                onBusyChange={setSavedCardBusy}
                                                payLabel={`Pay ${money(cardPortion)}`}
                                                disabled={walletPaying}
                                                charge={chargeSavedCard}
                                                onPaid={() => { setPaidHere(true); load(); }}
                                                onFailed={() => { /* the row states its own outcome */ }}
                                            />
                                        )}
                                    </div>
                                )}

                                <div className="pubpay__actions">
                                    {walletCovers ? (
                                        <button
                                            type="button"
                                            className="pubpay__btn pubpay__btn--primary"
                                            disabled={walletPaying || savedCardBusy}
                                            onClick={payFromWallet}
                                        >
                                            {walletPaying ? 'Paying from wallet…' : `Pay ${money(balanceDue)} from wallet`}
                                        </button>
                                    ) : !savedCardActive && (
                                        <button
                                            type="button"
                                            className="pubpay__btn pubpay__btn--primary"
                                            disabled={starting || walletPaying || savedCardBusy}
                                            onClick={() => (hostedRail !== 'off' ? setStep('card') : startPayment())}
                                        >
                                            {starting ? 'Opening secure payment…' : `Pay ${money(owner ? cardPortion : balanceDue)}`}
                                        </button>
                                    )}
                                    <a
                                        className="pubpay__btn pubpay__btn--ghost"
                                        href={`/api/pay-link/${token}/pdf`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <IconDoc />
                                        View invoice
                                    </a>
                                </div>
                            </>
                        )}

                        {/* The way in for the invoice's own client — never on a
                            guest invoice, where there is no account to sign in
                            to (owner, 2026-09-02). */}
                        {!paid && !expired && !owner && ctx && !info.is_guest && (
                            <p className="pubpay__signin">
                                <a href={`/login?next=${encodeURIComponent(window.location.pathname)}`}>Sign in</a>
                                {' '}to pay with your wallet balance or saved card.
                            </p>
                        )}

                        <div className="pubpay__foot">
                            <div className="pubpay__secure">
                                <IconLock />
                                Secure payment by Network International
                            </div>
                            {!paid && info.expires_at ? (
                                <div className="pubpay__validity">
                                    This link is valid until {fmtDate(info.expires_at)}.
                                </div>
                            ) : null}
                        </div>
                    </>
                )}
            </div>
            {/* The payer may have nothing but this link — no account, no app,
                no email thread. The same contact details the client Settings
                page offers, so there is always a way to reach the office. */}
            <div className="pubpay__pageFoot">
                <div className="pubpay__contact">
                    <a
                        className="pubpay__contactLink"
                        href="https://wa.me/971585891289"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        WhatsApp +971 58 589 1289
                    </a>
                    <a className="pubpay__contactLink" href="mailto:hello@holidayhomecleaning.ae">
                        hello@holidayhomecleaning.ae
                    </a>
                    <a
                        className="pubpay__contactLink"
                        href="https://holidayhomecleaning.ae"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        holidayhomecleaning.ae
                    </a>
                </div>
                <div>Holiday Home Cleaning Services Co. L.L.C · Dubai, UAE</div>
            </div>
        </div>
    );
}
